<template>
	<Teleport to="#navs">
		<div ref="floatyNav" :class="props.class">
			<slot></slot>
		</div>
	</Teleport>
</template>

<script setup>
/**
 * FloatyNav Component
 *
 * A floating navigation component that shows/hides based on scroll direction,
 * but only becomes active after the main header is scrolled out of view.
 *
 * Behavior:
 * - When header is visible: Nav stays hidden, no floaty behavior
 * - When header is off-screen: Nav shows/hides based on scroll direction
 * - Scroll down X pixels: Hide nav
 * - Scroll up X pixels: Show nav
 *
 * Uses Tailwind's '-translate-y-full' class to hide/show the nav
 */

import { ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { store } from '@js/vue/store.js'

// Props for customization
const props = defineProps({
	/** Number of pixels to scroll before triggering show/hide */
	threshold: {
		type: Number,
		default: 25
	},
	class: {
		type: String,
		default: ''
	}
})

// Template ref for the nav element
const floatyNav = ref(null)

// Scroll tracking variables
let lastScrollPosition = 0          // Previous scroll position
let lastScrollDiff = 0              // Previous scroll direction (+/- value)
let lastDirectionChangePosition = 0 // Scroll position when direction last changed
let ticking = false                 // Prevents multiple RAF calls
let headerHeight = 0                // Height of the main header

// Watch for changes to floatyNavEnabled in store
watch(() => store.floatyNavEnabled, (enabled) => {
	if (!enabled && floatyNav.value) {
		// Hide nav when floatyNav is disabled
		floatyNav.value.classList.add('-translate-y-full')
	}
})

/**
 * Get the height of the main header element
 * This determines when floaty behavior should activate
 */
const getHeaderHeight = () => {
	const header = document.querySelector('#hero')
	if (header) {
		headerHeight = header.offsetHeight
	}
}

/**
 * Main scroll handler - uses requestAnimationFrame for performance
 * Only applies floaty behavior when header is completely off-screen
 */
const handleScroll = () => {
	// Throttle scroll events with requestAnimationFrame
	if (!ticking && store.floatyNavEnabled && !store.navDrawerOpen) {
		requestAnimationFrame(() => {
			// Safety check - component might be unmounted
			if (!floatyNav.value) return

			const currentScroll = window.pageYOffset || document.documentElement.scrollTop
			const scrollDiff = currentScroll - lastScrollPosition

			// PHASE 1: Header still visible - disable floaty behavior
			if (currentScroll < headerHeight) {
				// Force nav to stay hidden while header is visible
				floatyNav.value.classList.add('-translate-y-full')

				// Update position tracking but skip floaty logic
				lastScrollPosition = currentScroll
				lastScrollDiff = scrollDiff
				lastDirectionChangePosition = currentScroll
				ticking = false
				return
			}

			// PHASE 2: Header off-screen - enable floaty behavior

			// Ignore very small scroll changes (noise)
			const isSignificantScroll = Math.abs(scrollDiff) > props.threshold

			if (!isSignificantScroll) {
				ticking = false
				return
			}

			// Detect scroll direction changes (only for significant scrolls)
			const isScrollingDown = scrollDiff > 0
			const wasScrollingDown = lastScrollDiff > 0
			const directionChanged = (isScrollingDown !== wasScrollingDown) && lastScrollDiff !== 0

			if (directionChanged) {
				lastDirectionChangePosition = lastScrollPosition
			}

			// Apply show/hide logic based on scroll distance since direction change
			const scrolledDownThreshold = currentScroll > lastDirectionChangePosition + props.threshold
			const scrolledUpThreshold = currentScroll < lastDirectionChangePosition - props.threshold

			if (isScrollingDown && scrolledDownThreshold) {
				// Scrolled down enough - hide nav
				floatyNav.value.classList.add('-translate-y-full')
			} else if (!isScrollingDown && scrolledUpThreshold) {
				// Scrolled up enough - show nav
				floatyNav.value.classList.remove('-translate-y-full')
			}

			// Update tracking variables for next iteration
			lastScrollDiff = scrollDiff
			lastScrollPosition = currentScroll
			ticking = false
		})
		ticking = true
	}
}

/**
 * Handle window resize - recalculate header height
 * Header height may change on mobile/tablet orientation changes
 */
const handleResize = () => {
	getHeaderHeight()
}

onMounted(() => {
	// Initialize header height measurement
	getHeaderHeight()

	// Set up event listeners with passive option for better performance
	window.addEventListener('scroll', handleScroll, { passive: true })
	window.addEventListener('resize', handleResize, { passive: true })

	// Initialize scroll position tracking
	const initialScroll = window.pageYOffset || document.documentElement.scrollTop
	lastScrollPosition = initialScroll
	lastDirectionChangePosition = initialScroll
	lastScrollDiff = 0

	// Set initial nav state - hidden by default
	if (floatyNav.value) {
		floatyNav.value.classList.add('-translate-y-full')
	}
})

onBeforeUnmount(() => {
	// Clean up event listeners to prevent memory leaks
	window.removeEventListener('scroll', handleScroll)
	window.removeEventListener('resize', handleResize)
})
</script>
